(() => {
  'use strict';
  if (window.__miniNestHero) {
    window.__miniNestHero.mount();
    return;
  }
  const instances = new WeakMap();
  const activeInstances = () => [...document.querySelectorAll('[data-mn-hero]')].map(root => instances.get(root)).filter(Boolean);
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const safeUrl = value => {
    try {
      const url = new URL(value, window.location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  };
  async function request(url, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal
      });
      let result;
      try { result = await response.json(); } catch (_) { throw new Error('The store returned an unexpected response. Please try again.'); }
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Your cart could not be updated. Please try again.');
      return result;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The store took too long to respond. Check your cart before trying again.');
      if (error instanceof TypeError) throw new Error('Unable to connect to the store. Check your connection and try again.');
      throw error;
    } finally { clearTimeout(timer); }
  }
  class MiniNestHero {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.mn-hero-form');
      this.drawer = root.querySelector('[data-mn-drawer]');
      this.busy = false;
      this.cartVersion = 0;
      this.cart = null;
      this.slides = [...root.querySelectorAll('[data-mn-slide]')];
      this.thumbnails = [...root.querySelectorAll('[data-mn-image]')];
      this.currency = root.dataset.currency || 'USD';
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.currency });
      this.previousOverflow = '';
      this.drawer.addEventListener('close', () => this.onClose());
      this.drawer.addEventListener('click', event => {
        if (event.target !== this.drawer) return;
        const rect = this.drawer.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.drawer.close();
      });
      const selected = root.querySelector('[data-mn-variant]:checked');
      if (selected) this.selectVariant(selected, false);
      else if (this.slides[0]) this.showImage(this.slides[0].dataset.mnSlide);
      this.refreshCart().catch(() => {});
    }
    endpoint(name) {
      const value = this.root.dataset[name];
      return `${value.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    }
    money(cents) { return this.formatter.format(Number(cents || 0) / 100); }
    error(target, message = '') {
      const element = this.root.querySelector(`[data-mn-${target}-error]`);
      element.textContent = message;
      element.hidden = !message;
    }
    showImage(imageId, moveFocus = false) {
      let index = this.slides.findIndex(slide => slide.dataset.mnSlide === String(imageId));
      if (index < 0) index = 0;
      this.slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== index; });
      this.thumbnails.forEach((thumbnail, thumbnailIndex) => thumbnail.setAttribute('aria-pressed', String(thumbnailIndex === index)));
      this.imageIndex = index;
      if (moveFocus) this.thumbnails[index]?.focus({ preventScroll: true });
      const thumbnail = this.thumbnails[index];
      const strip = thumbnail?.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollLeft = thumbnail.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumbnail.clientWidth) / 2;
      }
    }
    stepImage(step) {
      if (!this.slides.length) return;
      const next = (this.imageIndex + step + this.slides.length) % this.slides.length;
      this.showImage(this.slides[next].dataset.mnSlide);
    }
    selectVariant(input, updateHistory = true) {
      this.root.dataset.variantId = input.value;
      this.form.dataset.variantId = input.value;
      this.showImage(input.dataset.imageId);
      const submit = this.root.querySelector('[data-mn-add]');
      submit.disabled = input.disabled || this.busy;
      this.root.querySelector('[data-mn-add-label]').textContent = input.disabled ? 'SOLD OUT' : this.busy ? 'ADDING…' : 'ADD TO CART';
      this.error('form');
      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState(window.history.state, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('mininest:variant:change', { bubbles: true, detail: { variantId: input.value, quantity: 1 } }));
    }
    setBusy(busy, label) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.drawer.setAttribute('aria-busy', String(busy));
      const selected = this.root.querySelector('[data-mn-variant]:checked');
      this.root.querySelector('[data-mn-add]').disabled = busy || !selected || selected.disabled;
      this.root.querySelector('[data-mn-add-label]').textContent = busy && label ? label : selected && !selected.disabled ? 'ADD TO CART' : 'SOLD OUT';
      this.root.querySelectorAll('[data-mn-cart-action]').forEach(button => { button.disabled = busy; });
      const checkout = this.root.querySelector('[data-mn-checkout]');
      checkout.setAttribute('aria-disabled', String(busy));
      checkout.tabIndex = busy ? -1 : 0;
    }
    async refreshCart() {
      const version = ++this.cartVersion;
      const cart = await request(this.endpoint('cartUrl'));
      if (version === this.cartVersion && this.root.isConnected) this.renderCart(cart);
      return cart;
    }
    renderCart(cart) {
      this.cart = cart;
      this.currency = cart.currency || this.currency;
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.currency });
      const items = this.root.querySelector('[data-mn-cart-items]');
      const focused = document.activeElement;
      const focusKey = focused?.dataset?.key;
      const focusAction = focused?.dataset?.mnCartAction;
      const fragment = document.createDocumentFragment();
      for (const item of cart.items || []) {
        const row = node('article', 'mn-hero-cart-item');
        const imageUrl = safeUrl(item.image || item.featured_image?.url || '');
        if (imageUrl) {
          const image = node('img', 'mn-hero-cart-image');
          image.src = imageUrl;
          image.alt = item.featured_image?.alt || item.product_title || 'Cart item';
          image.width = 76;
          image.height = 90;
          row.append(image);
        } else { row.append(node('span', 'mn-hero-cart-image')); }
        const content = node('div', 'mn-hero-cart-item-content');
        const title = node('a', 'mn-hero-cart-item-title', item.product_title || item.title);
        title.href = safeUrl(item.url || '/cart') || this.root.dataset.cartUrl;
        content.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') content.append(node('p', 'mn-hero-cart-variant', item.variant_title));
        if (item.selling_plan_allocation?.selling_plan?.name) content.append(node('p', 'mn-hero-cart-variant', item.selling_plan_allocation.selling_plan.name));
        content.append(node('p', 'mn-hero-cart-price', this.money(item.final_line_price)));
        const controls = node('div', 'mn-hero-cart-controls');
        for (const [action, text, label] of [['decrease', '−', 'Decrease quantity'], ['quantity', String(item.quantity), ''], ['increase', '+', 'Increase quantity'], ['remove', 'Remove', 'Remove item']]) {
          if (action === 'quantity') {
            controls.append(node('span', 'mn-hero-cart-quantity', text));
            continue;
          }
          const button = node('button', action === 'remove' ? 'mn-hero-cart-remove' : 'mn-hero-cart-quantity-button', text);
          button.type = 'button';
          button.dataset.mnCartAction = action;
          button.dataset.key = item.key;
          button.dataset.quantity = String(item.quantity);
          button.setAttribute('aria-label', `${label}: ${item.title}`);
          button.disabled = this.busy;
          controls.append(button);
        }
        content.append(controls);
        row.append(content);
        fragment.append(row);
      }
      if (!cart.item_count) fragment.append(node('p', 'mn-hero-cart-empty', 'Your cart is empty.'));
      items.replaceChildren(fragment);
      this.root.querySelector('[data-mn-subtotal]').textContent = this.money(cart.total_price);
      this.root.querySelector('[data-mn-cart-footer]').hidden = !cart.item_count;
      document.querySelectorAll('[data-mn-cart-count]').forEach(count => { count.textContent = `(${cart.item_count})`; });
      document.querySelectorAll('[data-mn-cart-count-value]').forEach(count => { count.textContent = String(cart.item_count); });
      if (focusKey && this.drawer.open) {
        const replacement = [...items.querySelectorAll('[data-mn-cart-action]')].find(button => button.dataset.key === focusKey && button.dataset.mnCartAction === focusAction);
        (replacement || this.root.querySelector('[data-mn-close-cart]')).focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('mininest:cart:updated', { detail: { cart } }));
    }
    openDrawer() {
      if (this.drawer.open) return;
      if (typeof this.drawer.showModal !== 'function') { window.location.href = this.root.dataset.cartUrl; return; }
      this.returnFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.drawer.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.root.querySelector('[data-mn-close-cart]').focus({ preventScroll: true });
    }
    onClose() {
      document.documentElement.style.overflow = this.previousOverflow;
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
    async openCart() {
      this.openDrawer();
      if (this.busy) return;
      this.error('cart');
      this.root.querySelector('[data-mn-cart-status]').textContent = 'Loading your cart…';
      try {
        await this.refreshCart();
        this.root.querySelector('[data-mn-cart-status]').textContent = '';
      } catch (error) {
        this.error('cart', error.message);
        this.root.querySelector('[data-mn-cart-status]').textContent = '';
        if (!this.cart) {
          const link = node('a', 'mn-hero-cart-link', 'Open your cart');
          link.href = this.root.dataset.cartUrl;
          this.root.querySelector('[data-mn-cart-items]').replaceChildren(link);
        }
      }
    }
    async add() {
      if (this.busy) return;
      const variant = this.root.querySelector('[data-mn-variant]:checked');
      if (!variant || variant.disabled) return;
      this.error('form');
      this.error('cart');
      this.setBusy(true, 'ADDING…');
      ++this.cartVersion;
      let added = false;
      try {
        await request(this.endpoint('cartAddUrl'), { items: [{ id: variant.value, quantity: 1 }] });
        added = true;
        this.openDrawer();
        this.root.querySelector('[data-mn-cart-status]').textContent = 'Added to your cart.';
        await this.refreshCart();
      } catch (error) {
        if (added) {
          this.error('cart', `Your item was added. ${error.message}`);
          const link = node('a', 'mn-hero-cart-link', 'View your updated cart');
          link.href = this.root.dataset.cartUrl;
          this.root.querySelector('[data-mn-cart-items]').replaceChildren(link);
        } else { this.error('form', error.message); }
      } finally { this.setBusy(false); }
    }
    async change(button) {
      if (this.busy) return;
      const current = Number(button.dataset.quantity);
      const action = button.dataset.mnCartAction;
      const quantity = action === 'remove' ? 0 : Math.max(0, current + (action === 'increase' ? 1 : -1));
      this.error('cart');
      this.setBusy(true);
      this.root.querySelector('[data-mn-cart-status]').textContent = 'Updating your cart…';
      ++this.cartVersion;
      try {
        const cart = await request(this.endpoint('cartChangeUrl'), { id: button.dataset.key, quantity });
        this.setBusy(false);
        this.renderCart(cart);
        if (this.drawer.open) {
          const nextControl = [...this.root.querySelectorAll('[data-mn-cart-action]')].find(control => control.dataset.key === button.dataset.key && control.dataset.mnCartAction === action);
          (nextControl || this.root.querySelector('[data-mn-close-cart]')).focus({ preventScroll: true });
        }
        this.root.querySelector('[data-mn-cart-status]').textContent = quantity ? 'Cart updated.' : 'Item removed from your cart.';
      } catch (error) {
        this.error('cart', error.message);
        this.root.querySelector('[data-mn-cart-status]').textContent = '';
        try { await this.refreshCart(); } catch (_) {}
      } finally { this.setBusy(false); }
    }
  }
  function mount(scope = document) {
    const roots = [...scope.querySelectorAll('[data-mn-hero]')];
    if (scope.matches?.('[data-mn-hero]')) roots.unshift(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new MiniNestHero(root)); });
  }
  window.__miniNestHero = { mount, open: () => activeInstances()[0]?.openCart() };
  document.addEventListener('submit', event => {
    const form = event.target.closest?.('.mn-hero-form');
    const instance = form && instances.get(form.closest('[data-mn-hero]'));
    if (!instance) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    instance.add();
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-mn-variant]')) return;
    instances.get(event.target.closest('[data-mn-hero]'))?.selectVariant(event.target);
  });
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-mn-image],[data-mn-gallery-step],[data-mn-open-cart],[data-mn-close-cart],[data-mn-cart-action],[data-mn-checkout]');
    if (!button) return;
    const instance = instances.get(button.closest('[data-mn-hero]')) || activeInstances()[0];
    if (!instance) return;
    if (button.matches('[data-mn-checkout]')) { if (instance.busy) event.preventDefault(); return; }
    event.preventDefault();
    if (button.matches('[data-mn-open-cart]')) instance.openCart();
    else if (button.matches('[data-mn-close-cart]')) instance.drawer.close();
    else if (button.matches('[data-mn-image]')) instance.showImage(button.dataset.mnImage);
    else if (button.matches('[data-mn-gallery-step]')) instance.stepImage(Number(button.dataset.mnGalleryStep));
    else if (button.matches('[data-mn-cart-action]')) instance.change(button);
  });
  document.addEventListener('keydown', event => {
    if (!event.target.matches?.('[data-mn-image]')) return;
    const instance = instances.get(event.target.closest('[data-mn-hero]'));
    if (!instance) return;
    const keys = { ArrowLeft: -1, ArrowRight: 1 };
    if (keys[event.key]) {
      event.preventDefault();
      instance.stepImage(keys[event.key]);
      instance.thumbnails[instance.imageIndex]?.focus({ preventScroll: true });
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const index = event.key === 'Home' ? 0 : instance.slides.length - 1;
      instance.showImage(instance.slides[index]?.dataset.mnSlide, true);
    }
  });
  document.addEventListener('mininest:cart:open', () => activeInstances()[0]?.openCart());
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-mn-hero]').forEach(root => {
      const instance = instances.get(root);
      if (instance?.drawer.open) instance.drawer.close();
      instances.delete(root);
    });
  });
  window.addEventListener('pageshow', event => { if (event.persisted) activeInstances().forEach(instance => instance.refreshCart().catch(() => {})); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
